import { FileSearch, Layers } from "lucide-react";
import type * as React from "react";

import type { DealListItem } from "@/api/client";
import { EmptyState } from "@/components/ui/empty-state";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableRowLinkClassName,
} from "@/components/ui/table";
import { formatAud } from "@/lib/currency";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";
import { cn } from "@/lib/utils";

import { DealStatusBadge } from "./DealStatusBadge";

/** Sortable columns for the partner ledger (CHP-417). Status keeps its CHP-364
 * group toggle, so it is not a sortable column here. "archived" only renders in
 * the archive view (CHP-1032); "closed_won" only in the won view (CHP-1040). */
type PartnerDealSortColumn =
  "deal_id" | "client" | "value" | "registered" | "expiry" | "archived" | "closed_won";

/** The date a deal left the pipeline: closed_lost sets `closed_at`; denied
 * carries only the decision time `reviewed_at` (CHP-1032). */
function archivedAt(d: DealListItem): string | null {
  return d.closed_at ?? d.reviewed_at ?? null;
}

/** Module-level so the `useTableSort` memo dependency stays stable. */
function partnerDealSortValue(d: DealListItem, column: PartnerDealSortColumn): SortValue {
  switch (column) {
    case "deal_id":
      return d.deal_id_display;
    case "client":
      return d.client_name;
    case "value":
      return d.total_contract_value;
    case "registered":
      return d.created_at ? new Date(d.created_at) : null;
    case "expiry":
      return d.target_close_date ? new Date(d.target_close_date) : null;
    case "archived": {
      const archived = archivedAt(d);
      return archived ? new Date(archived) : null;
    }
    case "closed_won":
      return d.closed_at ? new Date(d.closed_at) : null;
  }
}

/** CHP-1038: a preset sort applied at mount (e.g. the expiring-deals scorecard
 * lands the Live tab soonest-expiry-first). Mount-time only — callers remount
 * the grid (key on the preset) to re-apply it. */
export interface PartnerDealPresetSort {
  column: PartnerDealSortColumn;
  direction: "asc" | "desc";
}

export interface DealsDataGridProps {
  deals: DealListItem[];
  /** Opens the deal in the slide-out detail drawer. */
  onDealClick: (dealId: string) => void;
  emptyMessage?: string;
  /** CHP-538: optional secondary line under the empty-state title. */
  emptyHint?: string;
  /** Optional CTA (e.g. a "Register a deal" button) shown in the empty state. */
  emptyAction?: React.ReactNode;
  /** CHP-364: whether rows are currently grouped by status (the caller sorts
   * them); drives the Status-header indicator. */
  statusGrouped?: boolean;
  /** CHP-364: clicking the Status column header toggles status grouping. */
  onToggleStatusGroup?: () => void;
  /** CHP-1032: Archive-tab rendering — Registration date + Expiry date are
   * replaced by a single "Date archived" column (`closed_at ?? reviewed_at`).
   * Callers should remount the grid when this flips (key on it) so a sort on
   * a column the other view doesn't render can't silently persist. */
  archiveView?: boolean;
  /** CHP-1040: Closed-won-tab rendering — the Expiry date column is replaced
   * by "Closed-won date" (`closed_at`); Registration date stays. Same remount
   * contract as `archiveView` (key on it). Mutually exclusive with
   * `archiveView` (a deal is never both won and archived). */
  wonView?: boolean;
  /** CHP-1038: mount-time sort preset (see {@link PartnerDealPresetSort}). */
  defaultSort?: PartnerDealPresetSort;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/**
 * Deal grid for the partner workspace (BDM/PO — identical structure for both).
 * Columns (CHP-315): Deal ID (opens the deal), Client, Value, Status,
 * Registration date, Expiry date. Reseller ID + Owner (CHP-307 / CHP-300
 * Fix 4) and the Quick Action column were removed; the close-deal action lives
 * in the detail drawer. In the archive view the two date columns collapse to
 * "Date archived" (CHP-1032); in the won view Expiry is replaced by
 * "Closed-won date" (CHP-1040).
 */
export function DealsDataGrid({
  deals,
  onDealClick,
  emptyMessage,
  emptyHint,
  emptyAction,
  statusGrouped,
  onToggleStatusGroup,
  archiveView = false,
  wonView = false,
  defaultSort,
}: DealsDataGridProps) {
  // CHP-417: client-side column sort. Unsorted, `sortedRows === deals`, so the
  // caller's default order (incl. CHP-364 status grouping) shows through.
  const { sort, toggle, sortedRows } = useTableSort(deals, partnerDealSortValue, defaultSort);

  if (deals.length === 0) {
    return (
      <EmptyState
        icon={FileSearch}
        title={emptyMessage ?? "No deals in this view."}
        hint={emptyHint}
        action={emptyAction}
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader column="deal_id" sort={sort} onToggle={toggle}>
            Deal ID
          </SortableHeader>
          <SortableHeader column="client" sort={sort} onToggle={toggle}>
            Client
          </SortableHeader>
          <SortableHeader column="value" sort={sort} onToggle={toggle} align="right">
            Value
          </SortableHeader>
          <TableHead scope="col">
            {onToggleStatusGroup ? (
              <button
                type="button"
                onClick={onToggleStatusGroup}
                aria-pressed={statusGrouped}
                data-testid="deal-status-group-toggle"
                className="flex items-center gap-1 hover:text-foreground"
              >
                Status
                <Layers
                  className={cn(
                    "h-3.5 w-3.5",
                    statusGrouped ? "text-primary" : "text-muted-foreground",
                  )}
                  aria-hidden
                />
              </button>
            ) : (
              "Status"
            )}
          </TableHead>
          {archiveView ? (
            <SortableHeader column="archived" sort={sort} onToggle={toggle}>
              Date archived
            </SortableHeader>
          ) : (
            <>
              <SortableHeader column="registered" sort={sort} onToggle={toggle}>
                Registration date
              </SortableHeader>
              {wonView ? (
                <SortableHeader column="closed_won" sort={sort} onToggle={toggle}>
                  Closed-won date
                </SortableHeader>
              ) : (
                <SortableHeader column="expiry" sort={sort} onToggle={toggle}>
                  Expiry date
                </SortableHeader>
              )}
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((d) => (
          <TableRow
            interactive
            key={d.id}
            data-testid={`deal-row-${d.id}`}
            // CHP-305: bind the open target per-row in JS, not via the CSS
            // `::after` stretched-link overlay alone. Safari/WebKit does not
            // honour `position: relative` on a <tr>, so every row's overlay
            // anchored to the same positioned ancestor (the .relative table
            // wrapper) and a single deal opened from any row.
            onClick={() => onDealClick(d.id)}
          >
            <TableCell>
              {/* Primary control — keyboard-focusable and named; the row
                  onClick above is the cross-browser pointer path (CHP-229,
                  CHP-305). */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDealClick(d.id);
                }}
                className={cn(
                  "font-medium text-primary underline-offset-4 hover:underline",
                  tableRowLinkClassName,
                )}
                data-testid={`deal-open-${d.id}`}
              >
                {d.deal_id_display ?? "View"}
              </button>
            </TableCell>
            <TableCell>{d.client_name}</TableCell>
            <TableCell className="text-right">{formatAud(d.total_contract_value)}</TableCell>
            <TableCell>
              <DealStatusBadge status={d.deal_status} />
            </TableCell>
            {archiveView ? (
              <TableCell data-testid={`deal-archived-${d.id}`}>
                {formatDate(archivedAt(d))}
              </TableCell>
            ) : (
              <>
                <TableCell data-testid={`deal-registered-${d.id}`}>
                  {formatDate(d.created_at)}
                </TableCell>
                {wonView ? (
                  <TableCell data-testid={`deal-closed-won-${d.id}`}>
                    {formatDate(d.closed_at ?? null)}
                  </TableCell>
                ) : (
                  <TableCell data-testid={`deal-expiry-${d.id}`}>
                    {formatDate(d.target_close_date)}
                  </TableCell>
                )}
              </>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
